import { useState } from 'react';
import {
    dateToMonthKey,
    getLastCompletedMonthKey,
    monthKeyToEndDate,
    monthKeyToStartDate,
    periodHeaderToMonthKey,
    subtractMonthsFromMonthKey,
} from '../../../../utils/dateUtils';

// UW's InsightsFinanceService caps its month-by-month scan at maxIterations = 120
// (InsightsFinanceService.ts:157, mirrored at :499) — the picker floor sits exactly at
// that boundary so the widest expressible range can never silently truncate.
export const BANK_RANGE_MAX_MONTHS = 120;

interface BankRangeState {
    orgId: string;
    startMonth: string;
    endMonth: string;
}

const EMPTY_MONTHS = { startMonth: '', endMonth: '' };

export interface BankDataEnvelope {
    /** "YYYY-MM" — earliest data month across the org's bank accounts. */
    floor: string;
    /** "YYYY-MM" — latest data month across the org's bank accounts. */
    ceiling: string;
}

interface EnvelopeAccountSummary {
    headers?: { key?: string; name?: string }[];
}

interface EnvelopeAccountOverview {
    closingBalanceDate?: string | null;
}

/**
 * Derives the org's bank-data envelope from a DEFAULT (no-range) bank statement summary
 * response — RECUR-28026 D14. Callers must only pass the unfiltered response: a
 * range-filtered response omits banks with no in-window data (D13) and would shrink the
 * envelope, re-introducing the dead-end this exists to fix.
 *
 * floor = the earliest data month across accounts, read from each account's first
 * `summaryData[account].headers` entry with `key === 'period'` — underwriting-engine trims
 * leading headers to that account's first data month (verified empirically: HDFC's first
 * header is `Aug_2025`, BoB's is `Mar_2025` — both their true data starts). If that backend
 * trimming behaviour ever changes, this derivation degrades to a wider-than-necessary floor
 * rather than a wrong one — the D14 safety net (BankDataTable's rangeApplied-aware empty
 * state) still prevents a dead-end even if the envelope is imprecise.
 * ceiling = the latest `closingBalanceDate` month across accounts, from `bankingOverview` —
 * each account's true last-data date.
 *
 * Returns null when it can't be derived (no accounts, or accounts with no headers/no
 * closing balance date) so the caller can fall back to the static bounds.
 */
export function deriveBankDataEnvelope(
    summaryData: EnvelopeAccountSummary[] | undefined,
    bankingOverview: EnvelopeAccountOverview[] | undefined,
): BankDataEnvelope | null {
    const floors = (summaryData || [])
        .map((account) => account.headers?.find((header) => header.key === 'period')?.name)
        .filter((name): name is string => !!name)
        .map(periodHeaderToMonthKey)
        .filter(Boolean);

    const ceilings = (bankingOverview || [])
        .map((account) => account.closingBalanceDate)
        .filter((date): date is string => !!date)
        .map(dateToMonthKey)
        .filter(Boolean);

    if (!floors.length || !ceilings.length) return null;

    // Union bounds across banks, not intersection: the envelope is [earliest floor, latest
    // ceiling] across ALL accounts, so any month at least one bank could have data in stays
    // selectable — a bank with a short history must not narrow the range for banks with a
    // longer one. Keys are "YYYY-MM" so plain string comparison is chronological comparison.
    // (The floor is separately clamped to `ceiling - 119 months` for the backend's hidden
    // 120-month cap — see `capFloor` in useBankDateRange below, not here.)
    return {
        floor: floors.reduce((min, key) => (key < min ? key : min)),
        ceiling: ceilings.reduce((max, key) => (key > max ? key : max)),
    };
}

/**
 * Org-keyed month-range state for the Banking "Time Period" control.
 * Holds {orgId, startMonth, endMonth} and DERIVES the applied months during render
 * whenever `orgId` matches the stored state's orgId — never via an effect, which would
 * race BankDataTable's fetch effect and fire one wasted request carrying the previous
 * org's range. On an org switch the mismatched orgId derives to EMPTY_MONTHS on
 * the very next render, before any effect runs.
 */
export function useBankDateRange(orgId: string, dataEnvelope?: BankDataEnvelope | null) {
    const [state, setState] = useState<BankRangeState>({ orgId, ...EMPTY_MONTHS });

    const { startMonth, endMonth } = state.orgId === orgId ? state : EMPTY_MONTHS;

    // When the org's actual bank-data envelope is known (derived from the default response —
    // RECUR-28026 D14), bound the pickers to it so a month with no data anywhere isn't
    // selectable in the first place. Falls back to the static 120-month bounds when the
    // envelope hasn't loaded yet or couldn't be derived — today's behaviour, unchanged.
    const staticCeiling = getLastCompletedMonthKey();
    const ceiling = dataEnvelope?.ceiling || staticCeiling;
    // Floor must never widen the span past the 120-month UW cap, even when the org's real
    // data goes back further than that — so it's the MORE RECENT (larger "YYYY-MM" string,
    // these sort lexically) of the envelope floor and `ceiling - 119 months`, computed
    // against the now-resolved `ceiling` above (not the static one) so a narrower/wider
    // envelope ceiling is respected too.
    const capFloor = subtractMonthsFromMonthKey(ceiling, BANK_RANGE_MAX_MONTHS - 1);
    const floor = dataEnvelope?.floor ? (dataEnvelope.floor > capFloor ? dataEnvelope.floor : capFloor) : capFloor;

    const selectStartMonth = (month: string) => setState({ orgId, startMonth: month, endMonth });
    const selectEndMonth = (month: string) => setState({ orgId, startMonth, endMonth: month });
    // Empties both months so `applied` goes empty by the same derivation above — the next
    // fetch sends NO date keys at all, i.e. the byte-identical default.
    const clear = () => setState({ orgId, ...EMPTY_MONTHS });

    // Both months required — monthKeyToStartDate/EndDate('') parse to a bogus year-2001
    // date (see dateUtils comment), not an empty range, so a one-sided pick must not apply.
    const hasBothMonths = !!startMonth && !!endMonth;
    const applied = {
        startDate: hasBothMonths ? monthKeyToStartDate(startMonth) : '',
        endDate: hasBothMonths ? monthKeyToEndDate(endMonth) : '',
    };

    return {
        startMonth,
        endMonth,
        // Start picker: floor at the 120-month cap, ceiling at the currently chosen end month.
        startPickerMin: floor,
        startPickerMax: endMonth || ceiling,
        // End picker: floor at the currently chosen start month, ceiling at last completed month.
        endPickerMin: startMonth || floor,
        endPickerMax: ceiling,
        selectStartMonth,
        selectEndMonth,
        clear,
        applied,
        rangeApplied: hasBothMonths,
    };
}
